import i18n from "i18next"

import { API_ROOT, tokenURL } from "../../api"
import {
  ApiResponseError,
  getErrorCode,
  parseApiError,
} from "../../api/apiUtils"
import {
  formatErrorResponse,
  getNetworkFailureUserMessage,
} from "../../api/utils"
import { HTTP_STATUS } from "../../constants"
import type {
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query"
import { BaseQueryFn, fetchBaseQuery } from "@reduxjs/toolkit/query/react"

// URLs that should not have the `lang` query parameter appended due to security reasons OR other restrictions
const langExemptUrls = [tokenURL()]

/**
 * Appends the `lang` query parameter to a given URL.
 * @param {string} url - The original URL.
 * @param {string} lang - The language code to append.
 * @returns {string} - The updated URL with the `lang` parameter.
 */
export const appendLangToUrl = (url: string): string => {
  if (langExemptUrls.includes(url)) {
    return url
  }

  const lang = i18n.language.split("-")[0]
  const parsedUrl = new URL(url, API_ROOT)
  parsedUrl.searchParams.set("lang", lang)
  return parsedUrl.toString()
}

/**
 * Handles API requests and responses, with consistent treatment of certain HTTP status codes.
 *
 * The module fetchBaseQueryExtended exports a wrapper function which leverages Redux Toolkit
 * and the fetchBaseQuery function from '@reduxjs/toolkit/query/react' to provide a custom API fetching solution.
 * The use of async/await syntax results in more readable asynchronous code.
 *
 * @module
 * @param {string|FetchArgs} args - The arguments for your request. Either a string (the URL), or an object with properties.
 * @param {object} api - An API service.
 * @param {object} extraOptions - Extra options for the fetch function.
 *
 * @returns {{}} response - The server response.
 *
 * @throws {Error} If the server responds with an error, it is thrown.
 * The handling of specific status codes should be noted:
 *  401 response causes the authorization to be cleared (via apiErrorHandlingMiddleware),
 *  402 prompts the function to attempt to interpret what feature is locked
 *  and change state to reflect this (via apiErrorHandlingMiddleware),
 *  502 responses are reshaped to surface a labeled "502 Bad Gateway" message.
 *
 * JOAN-10552: this base query no longer retries on any status code. If
 * 429/Retry-After ever needs auto-retry support, wire it here by
 * inspecting `response.error.status === HTTP_STATUS.TOO_MANY_REQUESTS`
 * and the `Retry-After` header from `response.meta?.response`.
 *
 **/

export const fetchBaseQueryExtended: BaseQueryFn<
  string | FetchArgs,
  unknown,
  ApiResponseError,
  object,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  // Modify `args` to include the `lang` query parameter
  if (typeof args === "string") {
    args = appendLangToUrl(args)
  } else if (args.url) {
    args.url = appendLangToUrl(args.url)
  }

  const baseQuery = fetchBaseQuery({
    baseUrl: API_ROOT,
    prepareHeaders: (headers, { getState }) => {
      const {
        auth: { access_token },
      } = getState() as any
      if (access_token) {
        headers.set("authorization", `Bearer ${access_token}`)
      }

      return headers
    },
  }) as BaseQueryFn<any, unknown, ApiResponseError, object, FetchBaseQueryMeta>

  const response = await baseQuery(args, api, extraOptions)

  // Handle successful response
  if (response.meta?.response?.ok) {
    return response
  }

  // Handle errors
  if (response.error) {
    /**
     * The cast above types `response.error` as `ApiResponseError`, but before
     * reshaping it is actually an RTK `FetchBaseQueryError`. Connectivity
     * failures (no response received) are signalled via a string `status`
     * (`FETCH_ERROR` / `TIMEOUT_ERROR`). JOAN-11550: keep that status in
     * `errorCode` for `isNetworkError`, and show a generic failure message when the
     * device is still online (raw "Failed to fetch" hides the real problem).
     */
    const rawError = response.error as unknown as FetchBaseQueryError
    const isFetchError =
      rawError.status === "FETCH_ERROR" || rawError.status === "TIMEOUT_ERROR"

    const errorResponse: ApiResponseError = {
      data: response.error.data,
      status: response.meta?.response?.status ?? 0,
      formError: parseApiError(response.error.data),
      message: isFetchError
        ? getNetworkFailureUserMessage(rawError.error)
        : formatErrorResponse(response.error.data),
      errorCode: isFetchError
        ? String(rawError.status)
        : getErrorCode(response),
      url: typeof args === "string" ? args : args.url,
    }

    // Special case: 502 Bad Gateway due to test backend
    if (response.error.status === HTTP_STATUS.BAD_GATEWAY) {
      errorResponse.formError = { _error: "502 Bad Gateway" }
      errorResponse.message = "502 Bad Gateway"
    }

    return { ...response, error: errorResponse }
  }

  return response
}
